import React from 'react';
import { RadioProps, Radio as SRadio } from 'semantic-ui-react';
import { List } from 'src/components/custom/index';

export type Option<T = any> = {
  label: React.ReactNode;
  value: T;
} & Record<string, any>;

export type RadioGroupProps<T = any> = {
  options?: Option<T>[];
  onChange?: (checked: T) => void;
  value?: T;
  onClick?: (data: Option<T>) => void;
  lineConnect?: boolean;
  className?: string;
  itemClassName?: string;
  renderItem?: (option: Option<T>, checked: boolean) => React.ReactElement;
  tail?: boolean;
};

type ExtraProps = {
  align?: 'center' | 'top';
  wrapperClassName?: string;
  extra?: React.ReactNode;
};

export interface IRadio {
  (props: RadioProps & ExtraProps): React.ReactElement;
  Group: (props: RadioGroupProps) => React.ReactElement;
  HorizontalGroup: (props: RadioGroupProps) => React.ReactElement;
}

const Radio: IRadio = (props) => {
  const { align, extra, wrapperClassName, onClick, ...rest } = props;

  return (
    <div
      className={`d-flex fw-normal ${wrapperClassName || ''} align-${align}`}
      onClick={(e) => onClick?.(e as any, {})}
    >
      <SRadio {...rest} />
      {extra}
    </div>
  );
};

function Group<T extends string | number>({
  value,
  options = [],
  onChange,
  onClick,
  lineConnect,
  className,
  renderItem,
  itemClassName,
  tail,
}: RadioGroupProps<T>) {
  const values =
    value || value === 0 ? ([] as (string | number)[]).concat(value) : [];

  return (
    <List
      tail={tail}
      className={`radio_group mb-2 ${
        lineConnect ? 'connect' : ''
      } ${className}`}
      columns={1}
      data={options}
      renderItem={
        renderItem
          ? (opt) => renderItem(opt, values.includes(opt.value))
          : (opt) => (
              <Radio
                className={itemClassName}
                key={opt.value}
                label={opt.label}
                value={opt.value}
                checked={values.includes(opt.value)}
                onClick={() => onClick?.(opt)}
                onChange={(e, d) => onChange?.(d.value as T)}
              />
            )
      }
    />
  );
}

function HorizontalGroup<T extends string | number>({
  value,
  options = [],
  onChange,
  onClick,
  lineConnect,
  className = '',
  renderItem,
  itemClassName,
}: RadioGroupProps<T>) {
  const values =
    value || value === 0 ? ([] as (string | number)[]).concat(value) : [];

  return (
    <div className={`d-flex-between flex-1 ${className}`}>
      {options?.map((opt) => {
        return (
          <div key={opt.value} className="d-flex flex-col align-center">
            <label className="mr-1 mb-12" htmlFor="">
              {opt.label}
            </label>
            <Radio
              className={itemClassName}
              key={opt.value}
              value={opt.value}
              checked={values.includes(opt.value)}
              onClick={() => onClick?.(opt)}
              onChange={(e, d) => onChange?.(d.value as T)}
            />
          </div>
        );
      })}
    </div>
  );
}

Radio.HorizontalGroup = HorizontalGroup;
Radio.Group = Group;

export default Radio;
